import { Component, type ErrorInfo, type ReactNode } from 'react';
import { reportClientError } from '../lib/report-error.js';

interface Props {
  children: ReactNode;
  /**
   * 'app' (default) renders a full-viewport screen; 'route' renders a COMPACT in-shell card so a
   * single page's render throw no longer white-screens the whole SPA - the sidebar/nav stay usable.
   */
  variant?: 'app' | 'route';
  /**
   * When this value changes the boundary clears its error. The route-level boundary passes the current
   * pathname so navigating away from a crashed page auto-recovers without a manual reload.
   */
  resetKey?: string;
}

interface State {
  error: Error | null;
}

/**
 * Error boundary. A single unguarded render throw would otherwise white-screen the whole SPA (React
 * unmounts the tree on an uncaught error). The app root wraps the tree with `variant="app"`; the
 * router additionally wraps each route with `variant="route"` + `resetKey={pathname}` so a page-level
 * crash is contained to the content area (nav preserved) and recovers on navigation.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface to the console for support; never rethrow.
    // eslint-disable-next-line no-console
    console.error('Render error caught by ErrorBoundary:', error, info.componentStack);
    // And tell the server, because a console message on a user's machine is not observability.
    reportClientError(error, { componentStack: info.componentStack ?? undefined });
  }

  override componentDidUpdate(prev: Props): void {
    // Auto-recover when the route (or other reset key) changes: leaving a crashed page restores a
    // working view without forcing a reload.
    if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: null });
  }

  private reset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    if (!this.state.error) return this.props.children;
    const route = this.props.variant === 'route';
    return (
      <div
        className={`flex flex-col items-center justify-center px-6 text-center ${
          route ? 'min-h-[60vh]' : 'min-h-dvh bg-bg-base'
        }`}
      >
        <div className="max-w-md">
          <div className="text-2xl font-semibold text-text-primary">Something went wrong</div>
          <p className="mt-2 text-sm text-text-secondary">
            This page hit an unexpected error. Your data is safe. Try again, reload, or head back to
            the homepage.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            {route && (
              <button
                onClick={this.reset}
                className="rounded-md bg-cta px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-cta-hover"
              >
                Try again
              </button>
            )}
            <button
              onClick={() => window.location.reload()}
              className={`rounded-md px-4 py-2 text-sm font-semibold transition-colors ${
                route
                  ? 'border border-border-default text-text-primary hover:bg-bg-overlay'
                  : 'bg-cta text-white hover:bg-cta-hover'
              }`}
            >
              Reload
            </button>
            <a
              href="/"
              onClick={this.reset}
              className="rounded-md border border-border-default px-4 py-2 text-sm font-medium text-text-primary transition-colors hover:bg-bg-overlay"
            >
              Go to homepage
            </a>
          </div>
        </div>
      </div>
    );
  }
}
